export const COMMISSION_OTHER_PLAN_KINDS = ['db_instagram_bounty'];
export const COMMISSION_OTHER_PLAN_KIND_LABELS = {
    db_instagram_bounty: 'designboom Readers Instagram — bounty per unit',
};
export const DEFAULT_DB_INSTAGRAM_TIERS = [
    { key: 'core', label: 'Core (1x Feed Post)', unitPrice: 399, bounty: 10 },
    { key: 'plus', label: 'Plus (Feed + Story)', unitPrice: 499, bounty: 25 },
    { key: 'supreme', label: 'Supreme (3x Feed Posts)', unitPrice: 999, bounty: 60 },
];
export function resolveCommissionOtherPlans(config) {
    return config?.commissionOtherPlans ?? [];
}
// ── Quarters ─────────────────────────────────────────────────────────────────
/** 'YYYY-Qn'. */
export function commissionOtherQuarterId(year, quarter) {
    return `${year}-Q${quarter}`;
}
export function commissionOtherParseQuarter(quarterId) {
    const m = /^(\d{4})-Q([1-4])$/.exec(quarterId);
    if (!m)
        throw new Error(`Bad quarter id: ${quarterId}`);
    return { year: Number(m[1]), quarter: Number(m[2]) };
}
/** "Q3 2026". */
export function commissionOtherQuarterLabel(quarterId) {
    const { year, quarter } = commissionOtherParseQuarter(quarterId);
    return `Q${quarter} ${year}`;
}
/** The three 'YYYY-MM' months of a quarter. */
export function commissionOtherQuarterMonths(quarterId) {
    const { year, quarter } = commissionOtherParseQuarter(quarterId);
    return [0, 1, 2].map((i) => `${year}-${String((quarter - 1) * 3 + i + 1).padStart(2, '0')}`);
}
/** First and last day (ISO) of the quarter. */
export function commissionOtherQuarterRange(quarterId) {
    const { year, quarter } = commissionOtherParseQuarter(quarterId);
    const m0 = (quarter - 1) * 3;
    const lastDay = new Date(Date.UTC(year, m0 + 3, 0)).getUTCDate();
    return { from: `${year}-${String(m0 + 1).padStart(2, '0')}-01`, to: `${year}-${String(m0 + 3).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}` };
}
/** The quarter has ended as of `todayISO` (Zurich date). */
export function commissionOtherQuarterEnded(quarterId, todayISO) {
    return todayISO > commissionOtherQuarterRange(quarterId).to;
}
/** Quarter a 'YYYY-MM-DD' falls in. */
export function commissionOtherQuarterOf(iso) {
    const y = Number(iso.slice(0, 4));
    const m = Number(iso.slice(5, 7));
    return commissionOtherQuarterId(y, Math.floor((m - 1) / 3) + 1);
}
// ── Unit classification (pure, testable) ─────────────────────────────────────
/**
 * Which tier a PayPal payment belongs to and how many units it holds. The Instagram tiers share
 * one `item_name` and differ only by price, so the GROSS identifies the tier: an exact multiple
 * of a list price (998 = 2 × Plus; 999 = 1 × Supreme — tolerance 0.5 so those stay apart), else
 * a 10 %-discounted multiple (the shop occasionally discounts; the revenue page notes it).
 * The `quantity` field is used only when it agrees with the price math. null = unclassified
 * (the line is listed with a warning and earns nothing until an admin maps it).
 */
export function classifyOtherUnits(gross, quantity, tiers) {
    if (!(gross > 0))
        return null;
    const tol = 0.5;
    const tryFactor = (factor) => {
        // Prefer the tier whose price divides the gross with the FEWEST units (one 999 unit
        // beats three 333s); ties are impossible with distinct prices.
        let best = null;
        for (const t of tiers) {
            if (!(t.unitPrice > 0))
                continue;
            const units = Math.round(gross / (t.unitPrice * factor));
            if (units < 1)
                continue;
            if (Math.abs(gross - units * t.unitPrice * factor) <= tol) {
                if (!best || units < best.units)
                    best = { tierKey: t.key, units };
            }
        }
        return best;
    };
    const exact = tryFactor(1);
    if (exact) {
        if (quantity && quantity > 1 && quantity !== exact.units) {
            // Price math wins; the quantity field is advisory (PayPal pushes often carry 1).
        }
        return exact;
    }
    return tryFactor(0.9);
}
export function commissionOtherReportId(quarterId, planKey) {
    return `${quarterId}__${planKey}`;
}
/** Aggregate classified lines into month + tier totals and the row figures. */
export function buildCommissionOtherRow(args) {
    const { plan, quarterId, lines, adjustment, adjustmentReason } = args;
    const months = commissionOtherQuarterMonths(quarterId);
    const tierTotals = (ls) => plan.tiers.map((t) => {
        const mine = ls.filter((l) => l.tierKey === t.key && !l.excluded);
        return {
            tierKey: t.key, label: t.label, unitPrice: t.unitPrice, bountyPerUnit: t.bounty,
            units: mine.reduce((s, l) => s + l.units, 0),
            revenue: Math.round(mine.reduce((s, l) => s + l.gross, 0) * 100) / 100,
            bounty: Math.round(mine.reduce((s, l) => s + l.bounty, 0) * 100) / 100,
        };
    });
    const monthRows = months.map((month) => {
        const byTier = tierTotals(lines.filter((l) => l.month === month));
        return { month, byTier, units: byTier.reduce((s, t) => s + t.units, 0), bounty: Math.round(byTier.reduce((s, t) => s + t.bounty, 0) * 100) / 100 };
    });
    const byTier = tierTotals(lines);
    const bountyFromLines = Math.round(byTier.reduce((s, t) => s + t.bounty, 0) * 100) / 100;
    const included = lines.filter((l) => !l.excluded && l.tierKey);
    return {
        planKey: plan.planKey,
        displayName: plan.displayName,
        userEmail: plan.userEmail ?? null,
        managerEmail: plan.managerEmail ?? null,
        currency: plan.currency,
        quarterId,
        months: monthRows,
        byTier,
        lines,
        bountyFromLines,
        adjustment,
        adjustmentReason,
        totalBounty: Math.round((bountyFromLines + adjustment) * 100) / 100,
        unclassifiedCount: lines.filter((l) => !l.excluded && !l.tierKey).length,
        notInvoicedCount: included.filter((l) => !l.invoiced).length,
    };
}
//# sourceMappingURL=commissionOther.js.map